import {
  Component,
  HostBinding,
  OnInit,
  computed,
  input,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AbEntrySearchService } from '@maximizer/core/global-search/data-access';
import { PipelineService } from '@maximizer/core/settings/data-access';
import { ListItem, Octopus } from '@maximizer/core/shared/domain';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { StringValidator } from '@maximizer/core/shared/ui/validators';
import { OpportunityService } from '@maximizer/outlook/opportunity/data-access';
import {
  AbEntryService,
  NavigationService,
  OutlookService,
  OutlookSessionService,
  OutlookStore,
} from '@maximizer/outlook/shared/data-access';
import {
  EntryType,
  OpportunityForm,
  OutlookAbEntrySearch,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DATEINPUTS } from '@progress/kendo-angular-dateinputs';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { catchError, finalize, forkJoin, map, Observable, of } from 'rxjs';
import {
  OpportunityModuleConfiguration,
  OpportunityStatusLabel,
} from './add-opportunity-actions';

@Component({
  selector: 'maximizer-add-opportunity',
  templateUrl: './add-opportunity.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DATEINPUTS,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class AddOpportunityComponent implements OnInit {
  @HostBinding('class.max-outlook') hostClass = true;

  notification =
    viewChild.required<OutlookNotificationComponent>('notification');

  id = input.required<string>();

  private readonly abEntryKey = computed(() => decodeURI(this.id()));

  abEntryList: ListItem<string>[] = [];
  autocompleteLoading = false;
  previousAutocompleteInput = '';
  form: FormGroupWrapper<OpportunityForm>;
  configuration: OpportunityModuleConfiguration;
  loading = true;
  canSave = false;
  fullFormat = 'dd MMMM yyyy';
  opportunityStatus = Octopus.OpportunityStatus;
  private _previousStatus: number = this.opportunityStatus.InProgress;

  constructor(
    private readonly session: OutlookSessionService,
    private readonly abEntryService: AbEntryService,
    private readonly abEntrySearchService: AbEntrySearchService,
    private readonly pipelineService: PipelineService,
    private readonly opportunityService: OpportunityService,
    private readonly translate: TranslateService,
    private readonly navigationService: NavigationService,
    private readonly outlookService: OutlookService,
    public outlookStore: OutlookStore,
  ) {
    this.configuration = {
      process: [],
      stages: [],
      statuses: [],
      abentries: [],
    };

    this.form = FormWrapperBuilder.group<OpportunityForm>(
      {
        key: null,
        objective: [
          null,
          {
            validators: Validators.compose([
              Validators.required,
              Validators.minLength(1),
              Validators.maxLength(253),
              StringValidator.NotEmpty(),
            ]),
          },
        ],
        abEntryKey: [null, [Validators.required]],
        abEntryName: null,
        companyName: null,
        companyKey: null,
        description: null,
        status: this.opportunityStatus.InProgress,
        processId: null,
        stageId: null,
        cost: null,
        actualRevenue: null,
        forecastRevenue: null,
        closeDate: null,
      },

      this.save,
      () => {
        this.form?.control.setErrors(null);
        if (this.form?.valid) {
          this.canSave = true;
        } else {
          this.canSave = false;
        }
      },
    );

    this.abEntryList = this.configuration.abentries.slice();
  }

  ngOnInit(): void {
    if (this.abEntryKey()) this.loadAbEntry();
    this.populateSubject();
  }

  async populateSubject(): Promise<void> {
    const subject = await this.outlookService.getSubject(true);

    this.form?.patch({
      objective: subject,
    });

    if (subject.trim() !== '') {
      this.form.group.get('objective')?.markAsTouched();
    }
  }

  handleProcessChange(process: string): void {
    const processObj = this.configuration?.process.find(
      (o) => o.id === process,
    );

    if (!processObj) {
      this.configuration.stages = [];
      this.form?.patch({
        processId: null,
        stageId: null,
      });
      return;
    }

    if (!processObj.stages) {
      this.form?.patch({
        processId: processObj.id,
        stageId: null,
      });
      return;
    }

    const sortedStages = [...processObj.stages].sort(
      (a, b) => a.probability - b.probability,
    );

    const firstStage = [...processObj.stages].reduce(
      (min, current) => (current.probability < min.probability ? current : min),
      processObj.stages[0],
    );

    this.configuration.stages = sortedStages.map((o) => ({
      id: o.id ?? '',
      name: o.name,
    }));
    this.form?.patch({
      processId: processObj.id,
      stageId: firstStage.id,
    });
  }

  handlePrimaryContactValueChange(input: string): void {
    const contactData = this.configuration.abentries.find(
      (entry) => entry.name === input,
    );

    if (contactData?.id) {
      this.abEntryService
        .getByKey(contactData.id)
        .pipe(
          catchError(() => {
            return of(null);
          }),
        )
        .subscribe((abEntry) => {
          if (abEntry) {
            const { primaryContactName, companyName } =
              this.determineContactFields(abEntry);

            this.form.patch({
              abEntryKey: contactData.id,
              abEntryName: primaryContactName,
              companyName: companyName,
              companyKey: abEntry.parentKey ?? '',
            });
          } else {
            this.form.patch({
              abEntryKey: contactData.id,
              abEntryName: contactData.name,
              companyName: '',
              companyKey: '',
            });
          }
        });
    } else {
      this.form.patch({
        abEntryKey: contactData?.id,
        abEntryName: contactData?.name,
      });
    }
  }

  handlePrimaryContactFilterChange(inputText: string): void {
    if (this.previousAutocompleteInput != inputText) {
      this.previousAutocompleteInput = inputText;
    }
    if (inputText.length < 3) {
      this.autocompleteLoading = false;
      return;
    }
    this.autocompleteLoading = true;

    setTimeout(() => {
      if (this.previousAutocompleteInput == inputText) {
        this.abEntrySearchService
          .search(inputText)
          .pipe(finalize(() => (this.autocompleteLoading = false)))
          .subscribe((data) => {
            if (data.length > 0) {
              this.configuration.abentries = data.map((ab) => ({
                id: ab.key ?? '',
                name: ab.name,
              }));
            }

            this.abEntryList = this.configuration.abentries.filter(
              (s) =>
                s.name.toLowerCase().indexOf(inputText.toLowerCase()) !== -1,
            );
          });
      }
    }, 1000);
  }

  handleStatusChange(status: number): void {
    if (status == this.opportunityStatus.Won) {
      this.form.patch({
        actualRevenue: this.form.value.forecastRevenue,
        forecastRevenue: null,
      });
    } else if (this._previousStatus == this.opportunityStatus.Won) {
      this.form.patch({
        forecastRevenue: this.form.value.actualRevenue,
        actualRevenue: null,
      });
    }
    this._previousStatus = status;
  }

  save(): void {
    if (!this.form) return;

    const validation = this.form.validate();
    if (!validation.valid) return;

    const opportunityLabel = this.translate.instant(
      'outlook.entries.opportunity',
    );

    this.loading = true;
    this.opportunityService
      .save(this.form.value)
      .pipe(
        catchError(async () => {
          this.notification().showMessage(
            'error',
            this.translate.instant('outlook.notification.createError', {
              type: opportunityLabel,
            }),
          );
        }),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe((result) => {
        if (result == null) return;

        this.notification().showMessage(
          'success',
          this.translate.instant('outlook.notification.createSuccess', {
            type: opportunityLabel,
          }),
        );
        this.navigationService.navigateBack();
      });
  }

  cancel(): void {
    this.navigationService.navigateBack();
  }

  private determineContactFields(abEntry: OutlookAbEntrySearch | null): {
    primaryContactName: string;
    companyName: string;
  } {
    let primaryContactName = '';
    let companyName = '';

    const entryType = abEntry?.type?.toLowerCase();

    if (entryType === EntryType.Contact) {
      primaryContactName = abEntry?.name ?? '';
      companyName = abEntry?.companyName ?? '';
    } else if (entryType === EntryType.Individual) {
      primaryContactName = abEntry?.name ?? '';
      companyName = abEntry?.name ?? '';
    } else if (entryType === EntryType.Company) {
      companyName = abEntry?.companyName ?? abEntry?.name ?? '';
    }

    return { primaryContactName, companyName };
  }

  private initStatuses(): Observable<ListItem<number>[]> {
    return this.translate.get('outlook.opportunity.statusValues').pipe(
      map((labels: OpportunityStatusLabel) => {
        return [
          { id: Octopus.OpportunityStatus.New, name: labels.new },
          { id: Octopus.OpportunityStatus.InProgress, name: labels.inProgress },
          { id: Octopus.OpportunityStatus.Won, name: labels.won },
          { id: Octopus.OpportunityStatus.Lost, name: labels.lost },
          { id: Octopus.OpportunityStatus.Abandoned, name: labels.abandoned },
          { id: Octopus.OpportunityStatus.Suspended, name: labels.suspended },
        ];
      }),
    );
  }

  private loadAbEntry(): void {
    this.configuration = {
      abentries: [],
      process: [],
      stages: [],
      statuses: [],
    };
    forkJoin({
      oppProcess: this.pipelineService.getPipelines(),
      currentUser: this.session.getCurrentUser(),
      statuses: this.initStatuses(),
      abentry: this.abEntryService.getByKey(this.abEntryKey()),
    })
      .pipe(
        catchError(async () => {
          this.notification().show('save', 'error');
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe((data) => {
        if (data == null) return;
        if (data.oppProcess.length <= 0) return;

        const sortedProcess = [...data.oppProcess].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        const sortedStatuses = [...data.statuses].sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        this.configuration = {
          process: sortedProcess,
          stages: [],
          statuses: sortedStatuses,
          abentries: [
            { id: this.abEntryKey(), name: data.abentry?.name ?? '' },
          ],
        };

        this.abEntryList = [
          { id: this.abEntryKey(), name: data.abentry?.name ?? '' },
        ];

        const { primaryContactName, companyName } = this.determineContactFields(
          data.abentry,
        );

        this.form?.patch({
          processId: null,
          stageId: null,
          companyName: companyName,
          companyKey: data.abentry?.parentKey ?? '',
          abEntryKey: this.abEntryKey(),
          abEntryName: primaryContactName,
        });
      });
  }
}
